export const protocolsData = [
  // ==================== GENERAL EMERGENCY ====================
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Malignant Hyperthermia',
    searchTerms: ['MH', 'dantrolene', 'rigidity', 'hyperthermia', 'hypercarbia', 'rhabdomyolysis'],
    content: {
      immediate: [
        'CALL FOR HELP - Announce "Malignant Hyperthermia"',
        'STOP all triggering agents (volatiles, succinylcholine)',
        'Hyperventilate with 100% O₂ at 10 L/min or more',
        'Get MH cart and start mixing dantrolene'
      ],
      emergencyContact: {
        name: 'MH Hotline',
        number: '1-800-644-9737',
        description: '24/7 expert consultation available'
      },
      recognition: [
        'Unexplained ↑ ETCO₂ despite increased ventilation (earliest, most sensitive sign)',
        'Tachycardia unresponsive to deepening anesthesia',
        'Masseter muscle rigidity after succinylcholine',
        'Generalized muscle rigidity',
        'Rapidly rising temperature (may be late finding)',
        'Mixed respiratory and metabolic acidosis',
        'Hyperkalemia with peaked T waves',
        'Myoglobinuria (dark, cola-colored urine)'
      ],
      treatment: [
        {
          action: 'Give Dantrolene 2.5 mg/kg IV push rapidly, repeat q5min until signs resolve',
          details: [
            'May require 10-30 mg/kg total dose',
            'If giving >10 mg/kg without symptom resolution, consider alternative diagnoses',
            'Continue dantrolene 1 mg/kg IV q6h for 24-48h post-crisis to prevent recrudescence'
          ]
        },
        {
          action: 'If surgery must continue, use non-triggering anesthesia',
          details: [
            'IV agents only: propofol, opioids, benzodiazepines, ketamine',
            'Non-depolarizing neuromuscular blockers safe',
            'NO volatile anesthetics or succinylcholine'
          ]
        },
        {
          action: 'Begin active cooling to temperature <38.5°C',
          details: [
            'Ice packs to groin, axillae, neck',
            'Cold IV saline (avoid Lactated Ringers if hyperkalemic)',
            'Lavage body cavities if available',
            'Stop cooling at 38°C to prevent overshoot'
          ]
        },
        {
          action: 'Treat hyperkalemia and acidosis',
          details: [
            'Calcium chloride 10 mg/kg IV (do NOT use calcium channel blockers with dantrolene)',
            'Insulin 0.1 units/kg + glucose 0.5 g/kg IV',
            'Sodium bicarbonate 1-2 mEq/kg IV',
            'Albuterol nebulizer 10-20 mg'
          ]
        },
        {
          action: 'Obtain arterial line and send labs',
          details: [
            'ABG every 15-30 min',
            'Electrolytes (especially K+)',
            'CK (will be markedly elevated)',
            'Myoglobin, urine myoglobin',
            'Coagulation studies'
          ]
        },
        {
          action: 'Place Foley catheter and ensure urine output >2 mL/kg/hr',
          details: [
            'Aggressive hydration to prevent myoglobin-induced renal failure',
            'Mannitol or furosemide if needed',
            'Monitor urine color (should clear with treatment)'
          ]
        },
        {
          action: 'Assess for extubation readiness carefully',
          details: [
            'Dantrolene causes muscle weakness that may impair respiratory function',
            'Consider keeping patient intubated until adequate strength returns',
            'Evaluate NIF, vital capacity, tidal volumes before extubation'
          ]
        },
        {
          action: 'Transfer to ICU when stable',
          details: [
            'Stability indicators: declining/normal ETCO₂, stable/decreasing HR, resolving hyperthermia, resolved rigidity',
            'See POST-ACUTE CARE section for ongoing management'
          ]
        }
      ],
      postAcuteCare: [
        {
          title: 'Continue dantrolene and monitor for recrudescence',
          content: 'Give dantrolene 1 mg/kg IV q6h for 24-48 hours. MH recrudesces in ~25% of cases despite initial response. Watch for rising temperature, tachycardia, increasing ETCO₂, or rigidity returning. Resume acute treatment if recrudescence occurs'
        },
        {
          title: 'ICU monitoring (minimum 24 hours)',
          content: 'Continuous monitoring: HR, core temperature, ETCO₂, minute ventilation, ABG, K⁺, CK, urine myoglobin'
        },
        {
          title: 'Fluid replacement',
          content: 'Maintain adequate IV fluid replacement. Dantrolene formulations containing mannitol cause significant diuresis. Monitor urine output and replace losses to prevent hypovolemia'
        },
        {
          title: 'Manage complications',
          content: 'Continue renal protection (urine output >2 mL/kg/hr), treat ongoing hyperkalemia/acidosis, monitor for DIC, follow CK levels (peak at 24-48h)'
        },
        {
          title: 'Family counseling and follow-up',
          content: 'Discuss genetic nature of MH (autosomal dominant), importance of family screening, avoid triggering agents in future. Contact MHAUS (1-800-644-9737) for referral to specialized MH testing center for caffeine-halothane contracture testing'
        },
        {
          title: 'Document and report',
          content: 'Complete MH incident report, notify MHAUS for registry, document all drugs/anesthetics given, provide patient with MH medical alert information'
        }
      ],
      drugs: [
        {
          name: 'Dantrolene (Dantrium/Revonto)',
          dose: '2.5 mg/kg IV initial bolus, repeat q5min until signs resolve (max ~10-30 mg/kg)',
          notes: 'Each vial = 20 mg powder. Reconstitute: Add 60 mL sterile water (without bacteriostatic agent), shake until clear. Time-consuming - assign dedicated personnel'
        },
        {
          name: 'Dantrolene (Ryanodex)',
          dose: '2.5 mg/kg IV initial bolus, repeat q5min until signs resolve (max ~10-30 mg/kg)',
          notes: 'Each vial = 250 mg powder. Reconstitute: Add 5 mL sterile water (without bacteriostatic agent), shake to orange-colored uniform suspension. Faster preparation than Dantrium'
        },
        {
          name: 'Calcium Chloride',
          dose: '10 mg/kg IV (10-20 mg/kg for severe hyperkalemia)',
          notes: 'For hyperkalemia. Do NOT give calcium channel blockers with dantrolene (risk of hyperkalemia, cardiovascular collapse)'
        },
        {
          name: 'Sodium Bicarbonate',
          dose: '1-2 mEq/kg IV',
          notes: 'For metabolic acidosis and hyperkalemia'
        },
        {
          name: 'Regular Insulin + Dextrose',
          dose: 'Insulin 0.1 units/kg + D50 0.5 g/kg (or D10 at 5 g/kg)',
          notes: 'For hyperkalemia'
        }
      ],
      differential: [
        'Sepsis or septic shock',
        'Thyroid storm',
        'Pheochromocytoma crisis',
        'Neuroleptic malignant syndrome',
        'Serotonin syndrome',
        'Cocaine or amphetamine toxicity',
        'Inadequate anesthesia with sympathetic surge'
      ],
      criticalChanges: [
        {
          condition: 'If cardiac arrest develops:',
          action: '→ VF/pVT Protocol or → PEA/Asystole Protocol. Continue dantrolene during CPR'
        },
        {
          condition: 'If severe refractory acidosis or hyperkalemia:',
          action: 'Consider emergency dialysis'
        }
      ],
      notes: [
        'Triggers: ALL volatile anesthetics (sevoflurane, desflurane, isoflurane) and succinylcholine',
        'Safe agents: propofol, all opioids, benzodiazepines, ketamine, all non-depolarizing NMBs',
        'Do NOT delay dantrolene while waiting for lab confirmation',
        'Genetic: autosomal dominant with variable penetrance (RYR1, CACNA1S genes)',
        'Mortality ~5% if treated early and aggressively (was 70% before dantrolene)',
        'Recrudescence occurs in ~25% of cases - hence 24-48h of continued dantrolene'
      ],
      references: [
        {
          title: 'MHAUS Emergency Clinical Guide',
          description: 'Malignant Hyperthermia Association of the United States - Primary clinical resource',
          url: 'https://www.mhaus.org/healthcare-professionals/managing-a-crisis/'
        },
        {
          title: 'Malignant Hyperthermia (Critical Care Medicine 2024)',
          description: 'Narrative review on clinical epidemiology and management by Pinyavat et al.',
          url: 'https://pubmed.ncbi.nlm.nih.gov/39171998/'
        }
      ],
      authors: ['BCM Anesthesia Crisis Manual Committee']
    }
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Local Anesthetic Systemic Toxicity (LAST)',
    searchTerms: ['LAST', 'lipid emulsion', 'intralipid', 'bupivacaine', 'ropivacaine', 'local anesthetic toxicity', 'seizure'],
    content: {
      immediate: [
        'STOP local anesthetic injection',
        'CALL FOR HELP - Get lipid emulsion',
        'Airway management, 100% O2',
        'Treat seizures with benzodiazepines',
        'LIPID EMULSION 20%: Bolus 1.5 mL/kg → Infusion 0.25 mL/kg/min',
        'If cardiac arrest: CPR + lipid (may need prolonged CPR)'
      ],
      recognition: [
        'CNS: circumoral numbness, metallic taste, tinnitus, confusion',
        'CNS progression: agitation, seizures, unconsciousness',
        'CV: bradycardia, hypotension, arrhythmias',
        'CV collapse: VT/VF, asystole, PEA'
      ],
      treatment: [
        { action: 'Lipid bolus 1.5 mL/kg over 1 min (~100 mL for 70 kg)' },
        { action: 'Start lipid infusion 0.25 mL/kg/min (~250 mL over 15 min for 70 kg)' },
        { action: 'May repeat bolus q3-5min if unstable, ↑ infusion to 0.5 mL/kg/min' },
        { action: 'Max dose: 12 mL/kg over 30 min' },
        { action: 'If arrest: Modified ACLS (small epi doses <1 mcg/kg, avoid vasopressin, use amiodarone not lidocaine)' }
      ],
      drugs: [
        {
          name: 'Lipid Emulsion 20%',
          dose: 'Bolus 1.5 mL/kg → Infusion 0.25 mL/kg/min',
          notes: 'May repeat bolus. Max 12 mL/kg over 30 min'
        },
        {
          name: 'Midazolam',
          dose: '1-2 mg IV for seizures',
          notes: 'Avoid large propofol doses'
        },
        {
          name: 'Epinephrine',
          dose: '<1 mcg/kg (small doses only)',
          notes: 'Avoid vasopressin'
        }
      ],
      notes: [
        'Lipid should be available wherever local anesthetics used',
        'Cardiotoxicity: Bupivacaine > Ropivacaine > Lidocaine',
        'May require prolonged CPR (>60 min)',
        'Post-event: Monitor 4-6h, watch for pancreatitis'
      ],
      references: [
        {
          title: 'ASRA LAST Checklist',
          description: 'American Society of Regional Anesthesia',
          url: 'https://www.asra.com/guidelines-articles/guidelines/guideline-item/guidelines/2020/11/01/checklist-for-treatment-of-local-anesthetic-systemic-toxicity'
        },
        {
          title: 'Lipid Rescue Registry',
          description: 'Report cases and access latest data',
          url: 'https://www.lipidrescue.org/'
        }
      ]
    }
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Anaphylaxis',
    searchTerms: ['anaphylaxis', 'allergic reaction', 'epinephrine', 'bronchospasm', 'hypotension', 'angioedema'],
    content: null
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Aspiration',
    searchTerms: ['aspiration', 'NPO', 'regurgitation', 'pneumonitis'],
    content: null
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Fire in OR',
    searchTerms: ['fire', 'airway fire', 'surgical fire', 'oxygen'],
    content: null
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Transfusion Reaction',
    searchTerms: ['transfusion', 'hemolytic', 'TRALI', 'TACO', 'blood reaction'],
    content: null
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Venous Air Embolism',
    searchTerms: ['VAE', 'air embolism', 'sitting position', 'neurosurgery'],
    content: null
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Amniotic Fluid Embolism',
    searchTerms: ['AFE', 'amniotic fluid', 'DIC', 'obstetric emergency'],
    content: null
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Massive Transfusion Protocol',
    searchTerms: ['MTP', 'massive transfusion', 'hemorrhage', 'bleeding', 'trauma'],
    content: null
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Total Spinal',
    searchTerms: ['total spinal', 'high spinal', 'neuraxial', 'epidural'],
    content: null
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Tension Pneumothorax',
    searchTerms: ['tension pneumothorax', 'needle decompression', 'chest tube'],
    content: null
  },
  {
    categoryId: 'general',
    category: 'General Emergency',
    title: 'Awareness Under Anesthesia',
    searchTerms: ['awareness', 'recall', 'inadequate anesthesia', 'BIS'],
    content: null
  },

  // ==================== PHYSIOLOGIC CRISES ====================
  {
    categoryId: 'physiologic',
    category: 'Physiologic Crises',
    title: 'Severe Intraoperative Hypotension',
    searchTerms: ['hypotension', 'shock', 'vasopressors', 'MAP'],
    content: null
  },
  {
    categoryId: 'physiologic',
    category: 'Physiologic Crises',
    title: 'Severe Intraoperative Hypertension',
    searchTerms: ['hypertension', 'blood pressure', 'nicardipine', 'labetalol'],
    content: null
  },
  {
    categoryId: 'physiologic',
    category: 'Physiologic Crises',
    title: 'Severe Hypoxemia',
    searchTerms: ['hypoxemia', 'desaturation', 'oxygen', 'ventilation'],
    content: null
  },
  {
    categoryId: 'physiologic',
    category: 'Physiologic Crises',
    title: 'Bronchospasm',
    searchTerms: ['bronchospasm', 'wheezing', 'albuterol', 'asthma'],
    content: null
  },

  // ==================== AIRWAY EMERGENCIES ====================
  {
    categoryId: 'airway',
    category: 'Airway Emergencies',
    title: 'Cannot Intubate, Cannot Oxygenate (CICO)',
    searchTerms: ['CICO', 'difficult airway', 'cricothyrotomy', 'front of neck access', 'FONA'],
    content: null
  },
  {
    categoryId: 'airway',
    category: 'Airway Emergencies',
    title: 'Failed Intubation',
    searchTerms: ['failed intubation', 'difficult airway', 'video laryngoscopy', 'LMA'],
    content: null
  },
  {
    categoryId: 'airway',
    category: 'Airway Emergencies',
    title: 'Laryngospasm',
    searchTerms: ['laryngospasm', 'stridor', 'succinylcholine', 'airway obstruction'],
    content: null
  },

  // ==================== ACLS ALGORITHMS ====================
  {
    categoryId: 'acls',
    category: 'ACLS Algorithms',
    title: 'Ventricular Fibrillation / Pulseless Ventricular Tachycardia',
    searchTerms: ['VF', 'VT', 'ventricular fibrillation', 'ventricular tachycardia', 'defibrillation', 'shock', 'cardiac arrest'],
    content: {
      immediate: [
        'START CPR - high quality compressions immediately',
        'CALL FOR HELP - activate code team',
        'DEFIBRILLATE as soon as available',
        'Resume CPR immediately for 2 minutes'
      ],
      recognition: [
        'Pulseless patient (check pulse for no more than 10 seconds)',
        'VF: Chaotic, irregular waveform on monitor',
        'Pulseless VT: Regular wide-complex tachycardia without pulse',
        'Patient unresponsive, not breathing or only gasping'
      ],
      treatment: [
        {
          action: 'Verify VF/pVT on monitor and give SHOCK',
          details: [
            'Biphasic: Follow device recommendations (typically 120-200J)',
            'Monophasic: 360J',
            'Single shock approach - do NOT stack shocks',
            'Minimize pre-shock pause (<10 seconds)'
          ]
        },
        {
          action: 'Resume CPR immediately after shock × 2 minutes',
          details: [
            'High-quality compressions: rate 100-120/min, depth 2-2.4 inches',
            'Allow full chest recoil between compressions',
            'Minimize interruptions (<10 seconds)',
            'Rotate compressors every 2 minutes to prevent fatigue'
          ]
        },
        {
          action: 'Establish IV or IO access during CPR',
          details: [
            'Use largest, most accessible vein',
            'IO access if IV unsuccessful or delayed',
            'Flush all medications with 10-20 mL bolus'
          ]
        },
        {
          action: 'Recheck rhythm after 2 min CPR',
          details: [
            'If VF/pVT persists: Give 2nd SHOCK',
            'Resume CPR × 2 min immediately',
            'Give Epinephrine 1 mg IV/IO as soon as feasible after 2nd shock'
          ]
        },
        {
          action: 'Recheck rhythm after 2 min CPR',
          details: [
            'If VF/pVT persists: Give 3rd SHOCK',
            'Resume CPR × 2 min immediately',
            'Give Amiodarone 300 mg IV/IO or Lidocaine 1-1.5 mg/kg'
          ]
        },
        {
          action: 'Continue CPR cycles: SHOCK → CPR × 2 min → rhythm check',
          details: [
            'Epinephrine 1 mg IV/IO every 3-5 minutes',
            'May give 2nd dose Amiodarone 150 mg after 5th shock',
            'Consider advanced airway (but defer if interrupts CPR)',
            'After advanced airway: continuous compressions + 10 breaths/min'
          ]
        },
        {
          action: 'Treat reversible causes throughout resuscitation (H\'s & T\'s)',
          details: [
            'Hypovolemia → Volume resuscitation',
            'Hypoxia → Ensure adequate ventilation and oxygenation',
            'Hydrogen ion (acidosis) → Consider bicarbonate if prolonged arrest',
            'Hypo/Hyperkalemia → Check labs, treat electrolyte abnormalities',
            'Hypothermia → Rewarm if temperature <36°C',
            'Tension pneumothorax → Needle decompression',
            'Tamponade (cardiac) → Pericardiocentesis or surgical drainage',
            'Toxins → Antidotes if known ingestion',
            'Thrombosis (coronary/pulmonary) → Consider thrombolytics, cath lab'
          ]
        },
        {
          action: 'Monitor for ROSC',
          details: [
            'Check for pulse during rhythm checks',
            'Sudden sustained increase in ETCO₂ (typically >40 mmHg) suggests ROSC',
            'If ROSC achieved: proceed to post-cardiac arrest care'
          ]
        }
      ],
      drugs: [
        {
          name: 'Defibrillation',
          dose: 'Biphasic 120-200J per device, Monophasic 360J',
          notes: 'Single shock approach. May escalate energy on subsequent shocks per device. Prefer biphasic waveform'
        },
        {
          name: 'Epinephrine',
          dose: '1 mg IV/IO every 3-5 minutes',
          notes: 'Give after 2nd shock and continue throughout resuscitation. No maximum dose. High-dose epinephrine NOT recommended'
        },
        {
          name: 'Amiodarone',
          dose: '300 mg IV/IO first dose, then 150 mg second dose',
          notes: 'Give after 3rd shock. May repeat 150 mg after 5th shock. Alternative: Lidocaine'
        },
        {
          name: 'Lidocaine',
          dose: '1-1.5 mg/kg IV/IO first dose, then 0.5-0.75 mg/kg',
          notes: 'Alternative to amiodarone. Max 3 doses or 3 mg/kg total'
        },
        {
          name: 'Sodium Bicarbonate',
          dose: '1 mEq/kg IV',
          notes: 'NOT routinely recommended. Consider only for prolonged arrest with severe acidosis or hyperkalemia'
        },
        {
          name: 'Calcium',
          dose: 'Calcium chloride 10% 10-20 mL IV',
          notes: 'NOT routinely recommended. Use only for hyperkalemia, hypocalcemia, or calcium channel blocker overdose'
        },
        {
          name: 'Magnesium',
          dose: '2 g IV over 5-10 min',
          notes: 'Only for torsades de pointes. NOT for routine VF/pVT'
        }
      ],
      differential: [
        'Acute coronary syndrome / STEMI',
        'Massive pulmonary embolism',
        'Primary arrhythmia (long QT, Brugada, HCM)',
        'Electrolyte abnormalities (K+, Mg2+, Ca2+)',
        'Drug toxicity (local anesthetics, cyclic antidepressants, digoxin)',
        'Tension pneumothorax',
        'Cardiac tamponade',
        'Hypothermia',
        'Trauma with hemorrhage'
      ],
      criticalChanges: [
        {
          condition: 'If ROSC achieved:',
          action: 'Transition to post-cardiac arrest care protocol immediately'
        },
        {
          condition: 'If rhythm changes to asystole or PEA:',
          action: '→ Asystole Protocol or → PEA Protocol (continue CPR, epinephrine, no shocks)'
        },
        {
          condition: 'If refractory to standard therapy after 3 shocks:',
          action: 'Consider: changing pad position, double sequential defibrillation, esmolol for refractory VF, mechanical CPR if transport needed'
        },
        {
          condition: 'If ETCO₂ <10 mmHg after 20 minutes of CPR:',
          action: 'Reasonable to consider termination of resuscitation (use multimodal assessment)'
        }
      ],
      postAcuteCare: [
        {
          title: 'Optimize oxygenation and ventilation',
          content: 'Target SpO₂ 92-98% (avoid hyperoxia). Target ETCO₂ 35-40 mmHg (avoid hyperventilation). Consider arterial line for frequent ABGs'
        },
        {
          title: 'Hemodynamic management',
          content: 'Target MAP ≥65 mmHg. Use vasopressors (norepinephrine preferred) or inotropes as needed. Avoid hypotension. Consider arterial line and central access'
        },
        {
          title: 'Targeted Temperature Management (TTM)',
          content: 'Maintain constant temperature between 32-37.5°C for at least 36 hours for comatose patients. Avoid hyperthermia (>37.5°C). Gradual rewarming at 0.25-0.5°C/hr'
        },
        {
          title: 'Coronary angiography consideration',
          content: 'Emergent coronary angiography for STEMI or suspected cardiac etiology. Consider even without ST elevation if suspected ACS. Do not delay for comatose patients'
        },
        {
          title: 'Neurologic monitoring and prognostication',
          content: 'Continuous EEG monitoring for seizures. Do not prognosticate before 72 hours post-rewarming. Consider multimodal approach: exam, imaging, EEG, biomarkers (NSE, NfL)'
        },
        {
          title: 'Seizure management',
          content: 'Treat clinical seizures. Consider EEG for myoclonus evaluation. Use benzodiazepines, levetiracetam, or propofol for seizures'
        },
        {
          title: 'ICU transfer and ongoing care',
          content: 'Transfer to ICU capable of post-arrest care including TTM. Glucose control 140-180 mg/dL. Monitor for complications. Consider CT imaging to evaluate arrest etiology. Survivorship and rehabilitation planning'
        }
      ],
      notes: [
        'VF/pVT is the most survivable cardiac arrest rhythm when treated promptly',
        'High-quality CPR is the foundation - prioritize minimal interruptions',
        'Single shock + immediate CPR is superior to stacked shocks',
        'Defer intubation if it interrupts compressions - bag-mask is acceptable initially',
        'After intubation: continuous compressions at 100-120/min + ventilations 10/min',
        'ETCO₂ monitoring: Sudden rise >40 mmHg often indicates ROSC',
        'Waveform capnography values <10 mmHg suggest poor CPR quality or futility',
        'Mechanical CPR devices NOT recommended for routine use',
        'Vasopressin no longer recommended (removed from 2025 guidelines)',
        'Consider ECMO/ECPR for refractory arrest in select centers'
      ],
      references: [
        {
          title: 'Part 9: Adult Advanced Life Support (2025 AHA Guidelines)',
          description: 'American Heart Association CPR & Emergency Cardiovascular Care',
          url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000001376'
        },
        {
          title: 'Part 11: Post–Cardiac Arrest Care (2025 AHA Guidelines)',
          description: 'Comprehensive post-resuscitation care recommendations',
          url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000001375'
        }
      ],
      authors: ['BCM Anesthesia Crisis Manual Committee']
    }
  },
  {
    categoryId: 'acls',
    category: 'ACLS Algorithms',
    title: 'Pulseless Electrical Activity (PEA)',
    searchTerms: ['PEA', 'pulseless electrical activity', 'Hs and Ts', 'cardiac arrest'],
    content: {
      immediate: [
        'START CPR - high quality compressions immediately',
        'CALL FOR HELP - activate code team',
        'Establish IV/IO access',
        'Give Epinephrine 1 mg IV/IO as soon as feasible'
      ],
      recognition: [
        'Pulseless patient despite organized electrical activity on monitor',
        'Any rhythm except VF, pVT, or asystole',
        'May see: narrow or wide QRS, bradycardia, tachycardia, or any rhythm',
        'Patient unresponsive, not breathing or only gasping',
        'Confirm pulselessness (check <10 seconds)'
      ],
      treatment: [
        {
          action: 'Start high-quality CPR immediately',
          details: [
            'Compressions: rate 100-120/min, depth 2-2.4 inches',
            'Allow full chest recoil between compressions',
            'Minimize interruptions (<10 seconds)',
            'Rotate compressors every 2 minutes',
            'Continue CPR cycles of 2 minutes'
          ]
        },
        {
          action: 'Establish IV or IO access',
          details: [
            'Use largest, most accessible vein',
            'IO access if IV delayed',
            'Flush medications with 10-20 mL bolus'
          ]
        },
        {
          action: 'Give Epinephrine 1 mg IV/IO as soon as feasible',
          details: [
            'Repeat every 3-5 minutes throughout resuscitation',
            'No maximum dose',
            'Earlier administration associated with better outcomes in PEA'
          ]
        },
        {
          action: 'Aggressively search for and treat reversible causes (H\'s & T\'s)',
          details: [
            'This is the KEY to PEA management - identify and fix the cause',
            'See DIFFERENTIAL DIAGNOSIS and CRITICAL CHANGES sections',
            'Consider ultrasound to identify treatable causes if available'
          ]
        },
        {
          action: 'Consider advanced airway',
          details: [
            'Defer intubation initially if it interrupts high-quality CPR',
            'Bag-mask ventilation is acceptable',
            'After intubation: continuous compressions + 10 breaths/min',
            'Confirm placement with waveform capnography (ETCO₂)'
          ]
        },
        {
          action: 'Check rhythm every 2 minutes',
          details: [
            'If rhythm changes to VF/pVT: → VF/pVT Protocol (defibrillate)',
            'If asystole develops: continue CPR and epinephrine',
            'Check for pulse only if rhythm appears organized',
            'Minimize pulse check interruptions'
          ]
        },
        {
          action: 'Monitor for ROSC',
          details: [
            'Sustained ETCO₂ >40 mmHg suggests ROSC',
            'Check pulse if ETCO₂ rises or rhythm appears organized',
            'If ROSC: proceed to post-cardiac arrest care'
          ]
        },
        {
          action: 'Consider termination of resuscitation if appropriate',
          details: [
            'ETCO₂ <10 mmHg after 20 minutes suggests futility',
            'Use multimodal assessment (duration, rhythm, ETCO₂, reversible causes)',
            'Consider ECPR at specialized centers for select patients'
          ]
        }
      ],
      drugs: [
        {
          name: 'Epinephrine',
          dose: '1 mg IV/IO every 3-5 minutes',
          notes: 'Give as soon as feasible, then every 3-5 min. No maximum dose. High-dose epinephrine NOT recommended'
        },
        {
          name: 'Sodium Bicarbonate',
          dose: '1 mEq/kg IV',
          notes: 'Consider for: prolonged arrest, pre-existing metabolic acidosis, hyperkalemia, TCA overdose. NOT routine'
        },
        {
          name: 'Calcium Chloride 10%',
          dose: '10-20 mL (1-2 g) IV push',
          notes: 'Use for: hyperkalemia, hypocalcemia, calcium channel blocker toxicity. NOT routine'
        },
        {
          name: 'Treatment for Specific Causes',
          dose: 'Varies by etiology',
          notes: 'See H\'s & T\'s. Examples: fluid bolus for hypovolemia, needle decompression for tension PTX, thrombolytics for PE'
        }
      ],
      differential: [
        'Hypovolemia - Most common in trauma, GI bleed, ruptured AAA',
        'Hypoxia - Airway obstruction, severe bronchospasm, pneumonia',
        'Hydrogen ion (acidosis) - Prolonged arrest, DKA, toxins',
        'Hyper/Hypokalemia - Renal failure, DKA, medications',
        'Hypothermia - Exposure, submersion',
        'Tension pneumothorax - Trauma, mechanical ventilation, procedures',
        'Tamponade (cardiac) - Post-cardiac surgery, trauma, pericarditis',
        'Toxins - Drug overdose, local anesthetics, beta blockers, CCB',
        'Thrombosis (coronary) - Acute MI',
        'Thrombosis (pulmonary) - Massive PE'
      ],
      criticalChanges: [
        {
          condition: 'If tension pneumothorax suspected:',
          action: 'Immediate needle decompression (2nd intercostal space, midclavicular line or 5th ICS, anterior axillary line). Do NOT delay for imaging'
        },
        {
          condition: 'If cardiac tamponade suspected:',
          action: 'Emergency pericardiocentesis or surgical drainage if trained. Consider ultrasound guidance if available'
        },
        {
          condition: 'If massive PE suspected:',
          action: 'Consider thrombolytic therapy (tPA 50 mg IV bolus). May require prolonged CPR (>60 min). Consider ECMO/ECPR'
        },
        {
          condition: 'If hypovolemia suspected:',
          action: 'Rapid IV fluid boluses. Identify and control source of bleeding. Activate massive transfusion protocol if hemorrhagic shock'
        },
        {
          condition: 'If hyperkalemia suspected (K+ >6.5):',
          action: 'Calcium chloride 1-2 g IV immediately. Sodium bicarbonate 50 mEq IV. Insulin 10 units + D50 50 mL. Consider dialysis'
        },
        {
          condition: 'If drug toxicity suspected:',
          action: 'Lipid emulsion for local anesthetics. Glucagon for beta blockers. Calcium for CCB. Sodium bicarbonate for TCA. May need prolonged CPR'
        },
        {
          condition: 'If rhythm changes to VF/pVT:',
          action: '→ VF/pVT Protocol - defibrillate immediately'
        },
        {
          condition: 'If ROSC achieved:',
          action: 'Immediate post-cardiac arrest care. Continue treating underlying cause'
        }
      ],
      postAcuteCare: [
        {
          title: 'Continue treating the underlying cause',
          content: 'PEA has an identifiable cause in most cases. Continue aggressive management of H\'s & T\'s even after ROSC. Consider CT imaging to identify complications or ongoing pathology'
        },
        {
          title: 'Optimize oxygenation and ventilation',
          content: 'Target SpO₂ 92-98% (avoid hyperoxia). Target ETCO₂ 35-40 mmHg (avoid hyperventilation). Arterial line for frequent ABGs'
        },
        {
          title: 'Hemodynamic management',
          content: 'Target MAP ≥65 mmHg. Treat ongoing shock (hemorrhagic, obstructive, distributive). Use vasopressors (norepinephrine preferred) or inotropes as needed'
        },
        {
          title: 'Targeted Temperature Management (TTM)',
          content: 'Maintain constant temperature 32-37.5°C for ≥36 hours for comatose patients. Avoid hyperthermia. Gradual rewarming 0.25-0.5°C/hr'
        },
        {
          title: 'Definitive treatment of underlying cause',
          content: 'Coronary angiography for suspected ACS. CT for PE, aortic dissection. Surgery for tamponade, bleeding. Dialysis for severe hyperkalemia/toxins'
        },
        {
          title: 'Neurologic monitoring and prognostication',
          content: 'Continuous EEG for seizures. Do not prognosticate before 72 hours post-rewarming. Multimodal approach: exam, imaging, EEG, biomarkers'
        },
        {
          title: 'ICU transfer and ongoing care',
          content: 'Transfer to ICU with post-arrest care capabilities. Monitor for recurrent arrest. Address complications. Survivorship planning'
        }
      ],
      notes: [
        'PEA has a CAUSE - focus is on identifying and treating reversible causes',
        'Most common causes: hypovolemia, hypoxia, tension pneumothorax, tamponade, massive PE',
        'PEA has worse prognosis than VF/pVT but better than asystole',
        'Early epinephrine associated with improved outcomes in PEA',
        'Ultrasound can rapidly identify tamponade, hypovolemia, pneumothorax if available',
        'Pseudo-PEA: very weak pulse present but not palpable - same treatment',
        'Consider occult bleeding (ruptured AAA, ectopic pregnancy, GI bleed) in appropriate patients',
        'ETCO₂ <10 mmHg throughout suggests poor prognosis',
        'Do NOT give atropine for PEA (removed from guidelines)',
        'Some reversible causes (PE, tamponade) may require prolonged CPR while definitive treatment arranged'
      ],
      references: [
        {
          title: 'Part 9: Adult Advanced Life Support (2025 AHA Guidelines)',
          description: 'American Heart Association CPR & Emergency Cardiovascular Care',
          url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000001376'
        },
        {
          title: 'Part 11: Post–Cardiac Arrest Care (2025 AHA Guidelines)',
          description: 'Comprehensive post-resuscitation care recommendations',
          url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000001375'
        }
      ],
      authors: ['BCM Anesthesia Crisis Manual Committee']
    }
  },
  {
    categoryId: 'acls',
    category: 'ACLS Algorithms',
    title: 'Asystole',
    searchTerms: ['asystole', 'flatline', 'cardiac arrest'],
    content: {
      immediate: [
        'Verify asystole in 2 leads (check gain, check leads attached)',
        'START CPR - high quality compressions immediately',
        'CALL FOR HELP - activate code team',
        'Give Epinephrine 1 mg IV/IO as soon as feasible'
      ],
      recognition: [
        'Flat line on monitor (no electrical activity)',
        'Pulseless, unresponsive patient',
        'Not breathing or only gasping',
        'Confirm in at least 2 leads (ensure leads attached and gain appropriate)',
        'Worst prognosis of all cardiac arrest rhythms'
      ],
      treatment: [
        {
          action: 'Confirm true asystole',
          details: [
            'Check lead connections and monitor settings',
            'Increase gain on monitor to look for fine VF',
            'Verify in at least 2 leads',
            'If any doubt about fine VF: attempt defibrillation'
          ]
        },
        {
          action: 'Start high-quality CPR immediately',
          details: [
            'Compressions: rate 100-120/min, depth 2-2.4 inches',
            'Allow full chest recoil',
            'Minimize interruptions (<10 seconds)',
            'Rotate compressors every 2 minutes',
            'CPR is the most important intervention for asystole'
          ]
        },
        {
          action: 'Establish IV or IO access',
          details: [
            'Use largest, most accessible vein',
            'IO if IV delayed or unsuccessful',
            'Flush medications with 10-20 mL bolus'
          ]
        },
        {
          action: 'Give Epinephrine 1 mg IV/IO as soon as feasible',
          details: [
            'Repeat every 3-5 minutes',
            'No maximum dose',
            'Earlier administration may improve outcomes'
          ]
        },
        {
          action: 'Search for and treat reversible causes (H\'s & T\'s)',
          details: [
            'Asystole often represents end-stage of untreated cause',
            'Focus on most likely causes based on clinical context',
            'See DIFFERENTIAL DIAGNOSIS section'
          ]
        },
        {
          action: 'Consider advanced airway',
          details: [
            'Defer if interrupts CPR',
            'Bag-mask acceptable initially',
            'After intubation: continuous compressions + 10 breaths/min',
            'Confirm with waveform capnography'
          ]
        },
        {
          action: 'Check rhythm every 2 minutes',
          details: [
            'If rhythm changes to VF/pVT: → VF/pVT Protocol (defibrillate immediately)',
            'If organized rhythm develops: check for pulse',
            'Continue CPR cycles throughout'
          ]
        },
        {
          action: 'Monitor for ROSC and consider termination',
          details: [
            'ETCO₂ >40 mmHg may indicate ROSC - check pulse',
            'ETCO₂ <10 mmHg after 20 min suggests futility',
            'Consider termination if: prolonged asystole, no reversible causes, no ETCO₂ response',
            'ROSC from asystole is rare but possible'
          ]
        }
      ],
      drugs: [
        {
          name: 'Epinephrine',
          dose: '1 mg IV/IO every 3-5 minutes',
          notes: 'Give as soon as feasible. Repeat every 3-5 min. No maximum dose. High-dose NOT recommended'
        },
        {
          name: 'Sodium Bicarbonate',
          dose: '1 mEq/kg IV',
          notes: 'Consider for: prolonged arrest, pre-existing acidosis, hyperkalemia, TCA overdose. NOT routine'
        },
        {
          name: 'Calcium Chloride 10%',
          dose: '10-20 mL (1-2 g) IV',
          notes: 'Only for: hyperkalemia, hypocalcemia, calcium channel blocker toxicity. NOT routine'
        },
        {
          name: 'Atropine',
          dose: 'NOT recommended',
          notes: 'Removed from asystole algorithm - no proven benefit'
        },
        {
          name: 'Pacing',
          dose: 'NOT recommended',
          notes: 'Routine pacing for asystole is NOT beneficial and may be harmful'
        }
      ],
      differential: [
        'Hypoxia - Prolonged respiratory arrest, airway obstruction',
        'Hypovolemia - Massive hemorrhage, severe dehydration',
        'Hydrogen ion (acidosis) - Prolonged untreated arrest',
        'Hyper/Hypokalemia - Severe electrolyte disturbance',
        'Hypothermia - Severe (<28°C)',
        'Hypoglycemia - Severe, prolonged',
        'Toxins - Drug overdose (beta blockers, calcium channel blockers, digoxin)',
        'Thrombosis (massive MI or PE) - End-stage',
        'Tension pneumothorax - Bilateral or prolonged',
        'Tamponade - Severe, prolonged',
        'Primary cardiac event progressed to asystole',
        'Prolonged hypoperfusion from any cause'
      ],
      criticalChanges: [
        {
          condition: 'If any uncertainty whether asystole vs fine VF:',
          action: 'Attempt defibrillation. Do not withhold shock if any doubt. Turn up monitor gain to maximum'
        },
        {
          condition: 'If rhythm changes to VF/pVT:',
          action: '→ VF/pVT Protocol - defibrillate immediately and continue per VF/pVT algorithm'
        },
        {
          condition: 'If organized rhythm appears:',
          action: 'Check for pulse immediately. If pulse present, proceed to post-cardiac arrest care'
        },
        {
          condition: 'If reversible cause identified:',
          action: 'Treat aggressively while continuing CPR. See PEA Protocol for specific H & T treatments'
        },
        {
          condition: 'If ETCO₂ <10 mmHg after 20 minutes:',
          action: 'Reasonable to consider termination of resuscitation using multimodal assessment'
        },
        {
          condition: 'If hypothermic (temp <30°C):',
          action: 'Continue resuscitation during rewarming. "Not dead until warm and dead." May need prolonged CPR with ECMO rewarming'
        }
      ],
      postAcuteCare: [
        {
          title: 'ROSC from asystole is rare',
          content: 'If ROSC achieved, prognosis is guarded. Aggressively treat underlying cause. High likelihood of neurologic injury due to prolonged low-perfusion state'
        },
        {
          title: 'Identify and treat underlying cause',
          content: 'Asystole is usually the end result of progressive shock or untreated arrest. Investigate: MI, PE, hemorrhage, severe sepsis, toxins, hypothermia'
        },
        {
          title: 'Optimize oxygenation and ventilation',
          content: 'Target SpO₂ 92-98% (avoid hyperoxia). Target ETCO₂ 35-40 mmHg. Arterial line for frequent ABGs'
        },
        {
          title: 'Hemodynamic support',
          content: 'Often requires high-dose vasopressors (norepinephrine, vasopressin) and/or inotropes. Target MAP ≥65 mmHg. Treat underlying shock state'
        },
        {
          title: 'Targeted Temperature Management (TTM)',
          content: 'Maintain constant temperature 32-37.5°C for ≥36 hours for comatose patients. Avoid hyperthermia. Gradual rewarming 0.25-0.5°C/hr'
        },
        {
          title: 'Neurologic assessment',
          content: 'High risk for severe neurologic injury. Continuous EEG. Do not prognosticate before 72 hours post-rewarming. Multimodal approach'
        },
        {
          title: 'ICU care and family discussions',
          content: 'Transfer to ICU with post-arrest capabilities. Early goals of care discussions given poor prognosis. Organ donation consideration if appropriate'
        }
      ],
      notes: [
        'Asystole has the worst prognosis of all cardiac arrest rhythms',
        'Often represents prolonged untreated arrest or end-stage of another rhythm',
        'High-quality CPR is most important intervention - focus on minimal interruptions',
        'Always verify true asystole - turn up gain, check multiple leads, verify connections',
        'Fine VF can appear as asystole - when in doubt, attempt defibrillation',
        'Pacing NOT recommended for asystole (no evidence of benefit, removed from guidelines)',
        'Atropine NOT recommended for asystole (removed from guidelines)',
        'ROSC rates <5% in most studies',
        'ETCO₂ monitoring important: <10 mmHg suggests very poor prognosis',
        'Consider early termination discussions if prolonged asystole without reversible causes',
        'Exception: hypothermia - continue resuscitation until rewarmed',
        'Focus resources on high-quality CPR rather than advanced interventions'
      ],
      references: [
        {
          title: 'Part 9: Adult Advanced Life Support (2025 AHA Guidelines)',
          description: 'American Heart Association CPR & Emergency Cardiovascular Care',
          url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000001376'
        },
        {
          title: 'Part 11: Post–Cardiac Arrest Care (2025 AHA Guidelines)',
          description: 'Comprehensive post-resuscitation care recommendations',
          url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000001375'
        }
      ],
      authors: ['BCM Anesthesia Crisis Manual Committee']
    }
  },
  {
    categoryId: 'acls',
    category: 'ACLS Algorithms',
    title: 'Bradycardia with Pulse',
    searchTerms: ['bradycardia', 'atropine', 'pacing', 'heart block'],
    content: {
      immediate: [
        'Assess for pulse and adequate perfusion',
        'Place on continuous monitoring and pulse oximetry',
        'Establish IV access',
        'If unstable: Give Atropine 1 mg IV and prepare for pacing'
      ],
      recognition: [
        'Heart rate typically <50 bpm WITH symptoms',
        'Symptomatic bradycardia: HR slow enough to cause symptoms',
        'Unstable signs: hypotension, altered mental status, chest pain, acute heart failure, shock',
        'May see: sinus bradycardia, 2nd degree AV block, 3rd degree (complete) heart block',
        'Treatment is symptom-driven, NOT rate-driven'
      ],
      treatment: [
        {
          action: 'Assess for signs of instability',
          details: [
            'Hypotension (SBP <90 mmHg)',
            'Acutely altered mental status',
            'Signs of shock (cool extremities, decreased cap refill, decreased urine output)',
            'Ischemic chest discomfort',
            'Acute heart failure or pulmonary edema'
          ]
        },
        {
          action: 'Identify and treat underlying causes',
          details: [
            'Hypoxia → supplemental oxygen',
            'Beta blockers or calcium channel blockers → may need specific reversal',
            'Acute coronary syndrome → aspirin, cardiology consult',
            'Increased vagal tone → may respond to atropine',
            'Electrolyte abnormalities (hyperkalemia) → correct',
            'Hypothermia → rewarming'
          ]
        },
        {
          action: 'For UNSTABLE symptomatic bradycardia: Give Atropine',
          details: [
            'Atropine 1 mg IV bolus',
            'May repeat every 3-5 minutes',
            'Maximum total dose: 3 mg',
            'Atropine less likely to work for: 2nd degree type II, 3rd degree heart block, transplanted heart'
          ]
        },
        {
          action: 'If atropine ineffective or patient severely unstable: Transcutaneous Pacing',
          details: [
            'Place pacing pads (anterior-posterior or anterior-lateral)',
            'Start pacing at 60-80 bpm',
            'Increase output (mA) until electrical and mechanical capture achieved',
            'Confirm mechanical capture by checking pulse/BP',
            'Provide analgesia/sedation if patient conscious (pacing is painful)'
          ]
        },
        {
          action: 'Consider chronotropic infusion if pacing unavailable or as bridge',
          details: [
            'Dopamine 5-20 mcg/kg/min IV infusion, titrate to response',
            'OR Epinephrine 2-10 mcg/min IV infusion, titrate to response',
            'Continue until transvenous pacing or rhythm improves'
          ]
        },
        {
          action: 'Prepare for transvenous pacing if indicated',
          details: [
            'Consult cardiology',
            'Indicated for: refractory symptomatic bradycardia, high-grade AV block',
            'Temporary transvenous pacing as bridge to permanent pacemaker',
            'Consider underlying cause (MI, complete heart block, etc.)'
          ]
        },
        {
          action: 'For STABLE asymptomatic bradycardia: Observe',
          details: [
            'Continue monitoring',
            'Identify and treat reversible causes',
            'No immediate intervention needed for asymptomatic bradycardia',
            'Cardiology consultation for evaluation of conduction system disease'
          ]
        }
      ],
      drugs: [
        {
          name: 'Atropine',
          dose: '1 mg IV bolus, repeat every 3-5 minutes (max total 3 mg)',
          notes: 'First-line for unstable bradycardia. Less effective for high-grade AV blocks or infranodal blocks. May paradoxically worsen bradycardia in transplanted hearts'
        },
        {
          name: 'Dopamine',
          dose: '5-20 mcg/kg/min IV infusion',
          notes: 'Titrate to patient response. Taper slowly when improving. Mix: 400 mg in 250 mL (1600 mcg/mL) or 800 mg in 500 mL'
        },
        {
          name: 'Epinephrine',
          dose: '2-10 mcg/min IV infusion',
          notes: 'Alternative to dopamine. Titrate to response. Mix: 1 mg in 250 mL (4 mcg/mL)'
        },
        {
          name: 'Transcutaneous Pacing',
          dose: 'Rate 60-80 bpm, Output titrated to capture',
          notes: 'Increase mA until capture seen. Confirm mechanical capture with pulse check. Provide sedation (fentanyl, midazolam) if patient conscious'
        },
        {
          name: 'Calcium (for CCB overdose)',
          dose: 'Calcium chloride 10% 1-2 g (10-20 mL) IV over 5-10 min',
          notes: 'For calcium channel blocker toxicity. May repeat'
        },
        {
          name: 'Glucagon (for beta blocker overdose)',
          dose: '3-10 mg IV bolus, then 3-5 mg/hr infusion',
          notes: 'For beta blocker overdose causing bradycardia'
        }
      ],
      differential: [
        'Physiologic sinus bradycardia (athletes, sleep)',
        'Medications (beta blockers, calcium channel blockers, digoxin, antiarrhythmics)',
        'Acute coronary syndrome (especially inferior MI)',
        '1st degree AV block (benign, observe)',
        '2nd degree AV block Type I (Wenckebach) - usually benign',
        '2nd degree AV block Type II - high risk, often progresses to complete block',
        '3rd degree (complete) heart block - requires pacing',
        'Sick sinus syndrome',
        'Increased vagal tone (vasovagal, carotid sinus hypersensitivity)',
        'Hypothyroidism',
        'Hypothermia',
        'Hypoxia',
        'Hyperkalemia',
        'Increased intracranial pressure (Cushing reflex)'
      ],
      criticalChanges: [
        {
          condition: 'If patient becomes pulseless:',
          action: 'If PEA/asystole develops: → PEA Protocol or → Asystole Protocol. Start CPR immediately'
        },
        {
          condition: 'If 2nd degree Type II or 3rd degree heart block:',
          action: 'High risk for progression to asystole. Prepare for immediate pacing. Avoid atropine if infranodal block suspected. Urgent cardiology consultation'
        },
        {
          condition: 'If refractory to atropine and pacing unavailable:',
          action: 'Start chronotropic infusion immediately (dopamine or epinephrine). Arrange urgent transfer to facility with pacing capabilities'
        },
        {
          condition: 'If inferior STEMI with bradycardia:',
          action: 'Often responds to atropine. Activate cath lab. Bradycardia usually resolves with reperfusion'
        },
        {
          condition: 'If drug overdose suspected (beta blocker, CCB, digoxin):',
          action: 'Specific antidotes may be needed. Calcium for CCB, glucagon for beta blockers. May need prolonged support. Consider ECMO for severe refractory cases'
        }
      ],
      postAcuteCare: [
        {
          title: 'Determine need for permanent pacemaker',
          content: 'Cardiology evaluation for: complete heart block, symptomatic 2nd degree Type II, sick sinus syndrome, chronic symptomatic bradycardia. Many patients will need permanent pacemaker'
        },
        {
          title: 'Medication review',
          content: 'Review all medications. Discontinue or adjust doses of: beta blockers, calcium channel blockers, digoxin, antiarrhythmics. Avoid bradycardic agents if patient required intervention'
        },
        {
          title: 'Identify underlying cause',
          content: 'ECG for ischemia/infarction. Electrolytes (K+, Mg2+, Ca2+). Thyroid function. Digoxin level if applicable. Consider Lyme disease in endemic areas (can cause heart block)'
        },
        {
          title: 'Telemetry monitoring',
          content: 'Continuous cardiac monitoring until cause identified and treated. Watch for progression of AV block. Monitor response to chronotropic medications'
        },
        {
          title: 'Temporary pacing management',
          content: 'If transvenous pacing placed: verify capture and sensing. CXR to confirm lead position and rule out pneumothorax. Daily threshold checks. Plan for permanent pacemaker'
        },
        {
          title: 'Address precipitating factors',
          content: 'Treat MI if present. Correct electrolytes. Rewarm if hypothermic. Treat hypothyroidism. Consider drug toxicology screen if overdose suspected'
        }
      ],
      notes: [
        'Treatment is SYMPTOM-driven, not rate-driven. Many patients tolerate HR 40-50 without symptoms',
        'Asymptomatic bradycardia in athletes or during sleep is normal - no treatment needed',
        'Atropine works best for: sinus bradycardia, 1st degree AV block, 2nd degree Type I',
        'Atropine less effective for: 2nd degree Type II, 3rd degree block (infranodal)',
        'Do NOT give atropine to asymptomatic patients - may worsen condition',
        'Transcutaneous pacing is PAINFUL - provide sedation/analgesia if patient conscious',
        'Always confirm mechanical capture with pacing - seeing pacing spikes ≠ effective pacing',
        '2nd degree Type II and 3rd degree block often require permanent pacemaker',
        'Inferior MI commonly causes transient bradycardia (AV nodal ischemia) - usually resolves',
        'Anterior MI with bradycardia suggests extensive damage - poor prognosis',
        'Transplanted hearts do NOT respond to atropine (denervated)'
      ],
      references: [
        {
          title: 'Part 9: Adult Advanced Life Support (2025 AHA Guidelines)',
          description: 'American Heart Association CPR & Emergency Cardiovascular Care - Bradycardia Algorithm',
          url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000001376'
        }
      ],
      authors: ['BCM Anesthesia Crisis Manual Committee']
    }
  },
  {
    categoryId: 'acls',
    category: 'ACLS Algorithms',
    title: 'Tachycardia with Pulse (Stable)',
    searchTerms: ['tachycardia', 'SVT', 'atrial fibrillation', 'adenosine', 'cardioversion'],
    content: {
      immediate: [
        'Assess stability: if unstable → immediate synchronized cardioversion',
        'Place on continuous monitoring, establish IV access',
        'Obtain 12-lead ECG to identify rhythm',
        'Assess QRS width: narrow (<120 ms) or wide (≥120 ms)'
      ],
      recognition: [
        'Heart rate >100 bpm with pulse palpable',
        'Patient is STABLE (adequate perfusion, alert, no chest pain, no acute heart failure)',
        'Narrow complex: QRS <120 ms (usually supraventricular)',
        'Wide complex: QRS ≥120 ms (VT until proven otherwise, or SVT with aberrancy)',
        'Regular vs irregular rhythm determines management'
      ],
      treatment: [
        {
          action: 'Assess for signs of instability (if present → Unstable Tachycardia Protocol)',
          details: [
            'Hypotension',
            'Acutely altered mental status',
            'Signs of shock',
            'Ischemic chest discomfort',
            'Acute heart failure',
            'If ANY of these present: → Unstable Tachycardia Protocol (synchronized cardioversion)'
          ]
        },
        {
          action: 'Obtain 12-lead ECG and identify rhythm',
          details: [
            'Measure QRS width: <120 ms = narrow, ≥120 ms = wide',
            'Assess regularity: regular vs irregular',
            'Look for P waves',
            'Identify specific rhythm if possible'
          ]
        },
        {
          action: 'For NARROW QRS, REGULAR rhythm (likely SVT/PSVT):',
          details: [
            'Attempt vagal maneuvers first (see DRUG DOSING section)',
            'If vagal maneuvers fail: Adenosine 6 mg IV rapid push',
            'If no conversion: Adenosine 12 mg IV rapid push (may repeat × 1)',
            'If still no conversion: consider beta blockers or calcium channel blockers',
            'May need cardiology consultation for refractory cases'
          ]
        },
        {
          action: 'For NARROW QRS, IRREGULAR rhythm (likely A-fib with RVR):',
          details: [
            'Do NOT give adenosine',
            'Rate control with beta blockers or calcium channel blockers',
            'Metoprolol 5 mg IV over 2 min, may repeat q5min × 3',
            'OR Diltiazem 0.25 mg/kg IV over 2 min, then infusion 5-15 mg/hr',
            'Consider anticoagulation (assess stroke risk)',
            'Treat underlying cause (sepsis, hyperthyroidism, alcohol, etc.)'
          ]
        },
        {
          action: 'For WIDE QRS, REGULAR rhythm (VT vs SVT with aberrancy):',
          details: [
            'Assume VT until proven otherwise',
            'If uncertain etiology: adenosine 6 mg may be considered for diagnosis/treatment',
            'If confirmed VT: amiodarone 150 mg IV over 10 min, then infusion 1 mg/min × 6 hr',
            'OR Procainamide 20-50 mg/min IV until arrhythmia suppressed (max 17 mg/kg)',
            'OR Sotalol 100 mg IV over 5 min',
            'Consider elective synchronized cardioversion if refractory'
          ]
        },
        {
          action: 'For WIDE QRS, IRREGULAR rhythm (A-fib with aberrancy, pre-excited A-fib, or polymorphic VT):',
          details: [
            'Do NOT give adenosine, beta blockers, or calcium channel blockers',
            'If pre-excited A-fib (WPW): procainamide or amiodarone',
            'If polymorphic VT (torsades): magnesium 2 g IV, correct electrolytes, increase heart rate',
            'If irregular wide complex of uncertain etiology: expert consultation'
          ]
        },
        {
          action: 'Treat underlying causes',
          details: [
            'Hypoxia, hypovolemia, ischemia',
            'Hyperthyroidism, pheochromocytoma',
            'Electrolyte abnormalities (K+, Mg2+)',
            'Drug toxicity or withdrawal',
            'Pulmonary embolism',
            'Sepsis, fever, pain, anxiety'
          ]
        }
      ],
      drugs: [
        {
          name: 'Vagal Maneuvers',
          dose: 'Modified Valsalva: strain for 10-15 sec, then lay supine with legs raised. OR Carotid massage (if no carotid bruits)',
          notes: 'First-line for regular narrow complex tachycardia (SVT). Success rate ~20-30%. Most effective when done properly'
        },
        {
          name: 'Adenosine',
          dose: '6 mg IV rapid push, then 12 mg × 2 if needed',
          notes: 'For regular narrow complex tachycardia. MUST give rapid IV push followed by 20 mL saline flush. Brief asystole common. Warn patient. Half-life <10 sec. Avoid in asthma (bronchospasm), irregular rhythms, pre-excited A-fib'
        },
        {
          name: 'Metoprolol',
          dose: '5 mg IV over 2 minutes, repeat q5min × 3 (max 15 mg)',
          notes: 'For rate control of A-fib/A-flutter. Contraindications: heart failure, hypotension, severe COPD/asthma'
        },
        {
          name: 'Diltiazem',
          dose: '0.25 mg/kg IV over 2 min (typically 15-20 mg), then infusion 5-15 mg/hr',
          notes: 'Alternative for rate control. May repeat 0.35 mg/kg after 15 min if needed. Avoid with heart failure or hypotension'
        },
        {
          name: 'Amiodarone',
          dose: '150 mg IV over 10 minutes, then infusion 1 mg/min × 6 hr, then 0.5 mg/min',
          notes: 'For wide complex regular tachycardia (VT) or refractory SVT. Can cause hypotension. Mix in glass bottle or non-PVC bag'
        },
        {
          name: 'Procainamide',
          dose: '20-50 mg/min IV until arrhythmia suppressed, hypotension, QRS widens >50%, or max 17 mg/kg given',
          notes: 'Alternative for VT. Then maintenance infusion 1-4 mg/min. Watch for QT prolongation. Avoid if QTc prolonged'
        },
        {
          name: 'Sotalol',
          dose: '100 mg (1.5 mg/kg) IV over 5 minutes',
          notes: 'Alternative antiarrhythmic for VT. Prolongs QT. Avoid in long QT syndrome'
        },
        {
          name: 'Magnesium Sulfate',
          dose: '2 g IV over 5-10 minutes',
          notes: 'For torsades de pointes (polymorphic VT with long QT). May repeat'
        },
        {
          name: 'Synchronized Cardioversion (elective)',
          dose: 'Per device recommendations. Higher energy (≥200J) preferred for A-fib',
          notes: 'For stable patients when medications fail or as primary therapy for certain rhythms. Requires sedation. Ensure synchronized mode'
        }
      ],
      differential: [
        'Narrow complex regular: AVNRT, AVRT (WPW), atrial tachycardia, sinus tachycardia',
        'Narrow complex irregular: Atrial fibrillation, atrial flutter with variable block, MAT',
        'Wide complex regular: Ventricular tachycardia (most common), SVT with aberrancy, antidromic AVRT',
        'Wide complex irregular: A-fib with aberrancy, pre-excited A-fib (WPW), polymorphic VT (torsades)',
        'Sinus tachycardia (treat underlying cause, not the rhythm)',
        'Multifocal atrial tachycardia (MAT) - often in COPD patients',
        'Atrial flutter - "sawtooth" pattern on ECG'
      ],
      criticalChanges: [
        {
          condition: 'If patient becomes unstable at any time:',
          action: '→ Unstable Tachycardia Protocol - immediate synchronized cardioversion'
        },
        {
          condition: 'If wide complex irregular rhythm suspected to be pre-excited A-fib (WPW):',
          action: 'DO NOT give adenosine, beta blockers, calcium channel blockers, or digoxin (can cause VF). Use procainamide or amiodarone'
        },
        {
          condition: 'If polymorphic VT (torsades de pointes) with prolonged QT:',
          action: 'Magnesium 2 g IV immediately. Correct K+ >4, Mg2+ >2. Increase heart rate (pacing or isoproterenol). Stop QT-prolonging drugs'
        },
        {
          condition: 'If monomorphic VT that is stable:',
          action: 'Amiodarone or procainamide. If refractory, consider elective synchronized cardioversion with sedation'
        },
        {
          condition: 'If sinus tachycardia identified:',
          action: 'Do NOT treat the rhythm. Identify and treat underlying cause (pain, anxiety, fever, hypovolemia, hypoxia, PE, etc.)'
        },
        {
          condition: 'If rhythm unclear or polymorphic VT without pulse:',
          action: '→ VF/pVT Protocol - treat as VF with unsynchronized defibrillation'
        }
      ],
      postAcuteCare: [
        {
          title: 'Identify and treat underlying cause',
          content: 'Labs: electrolytes (K+, Mg2+, Ca2+), troponin, thyroid function. ECG for ischemia. Imaging if PE suspected. Treat infections, correct electrolytes, address toxins/drugs'
        },
        {
          title: 'Rhythm-specific management',
          content: 'A-fib: anticoagulation assessment (CHA2DS2-VASc score), rate vs rhythm control strategy. VT: echo, cardiac MRI, EP study. WPW: EP consultation for ablation. SVT: EP referral for ablation if recurrent'
        },
        {
          title: 'Medication management',
          content: 'If cardioverted: may need maintenance antiarrhythmic (amiodarone, sotalol, flecainide). Review all medications for QT prolongation. Avoid triggers (alcohol, caffeine, stimulants)'
        },
        {
          title: 'Anticoagulation for A-fib',
          content: 'If A-fib >48 hours or unknown duration: anticoagulation required before cardioversion (or TEE to rule out clot). Assess stroke risk with CHA2DS2-VASc. Consider NOAC vs warfarin'
        },
        {
          title: 'Outpatient monitoring and follow-up',
          content: 'Telemetry monitoring until stable. Holter monitor or event recorder for recurrent symptoms. Cardiology follow-up. EP consultation for recurrent SVT or VT'
        },
        {
          title: 'ICD consideration for VT',
          content: 'If sustained VT with structural heart disease, low EF, or aborted sudden death: ICD evaluation. Exclude reversible causes (ischemia, electrolytes) before ICD decision'
        }
      ],
      notes: [
        'Stable tachycardia means adequate perfusion - if ANY sign of instability, cardiovert immediately',
        'Wide complex tachycardia = VT until proven otherwise. Do not assume SVT with aberrancy',
        'Adenosine is DIAGNOSTIC and therapeutic - may unmask underlying rhythm (flutter, A-fib)',
        'Adenosine causes brief asystole/chest discomfort - warn patient and have defib ready',
        'Do NOT give adenosine for: irregular rhythms, pre-excited A-fib, wide complex irregular',
        'A-fib with RVR: treat underlying cause first (sepsis, hyperthyroidism, volume status)',
        'Sinus tachycardia is NOT a primary arrhythmia - always treat underlying cause',
        'WPW with A-fib is life-threatening - avoid AV nodal blocking agents (can cause VF)',
        'Elective cardioversion requires sedation and informed consent (unlike emergent)',
        'For polymorphic VT, check QT interval - if prolonged, it\'s torsades (needs magnesium)',
        'MAT (multifocal atrial tachycardia) often seen with COPD - treat underlying lung disease'
      ],
      references: [
        {
          title: 'Part 9: Adult Advanced Life Support (2025 AHA Guidelines)',
          description: 'American Heart Association CPR & Emergency Cardiovascular Care - Tachycardia Algorithm',
          url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000001376'
        }
      ],
      authors: ['BCM Anesthesia Crisis Manual Committee']
    }
  },
  {
    categoryId: 'acls',
    category: 'ACLS Algorithms',
    title: 'Tachycardia with Pulse (Unstable)',
    searchTerms: ['unstable tachycardia', 'cardioversion', 'synchronized shock'],
    content: {
      immediate: [
        'Identify instability (hypotension, AMS, chest pain, heart failure, shock)',
        'Place on continuous monitoring, establish IV access',
        'Prepare for IMMEDIATE synchronized cardioversion',
        'Consider sedation if time permits - do NOT delay cardioversion'
      ],
      recognition: [
        'Heart rate >100 bpm with pulse AND signs of instability',
        'UNSTABLE means: hypotension, acutely altered mental status, chest pain, acute heart failure, or shock',
        'Tachycardia is CAUSING the instability (not just associated with it)',
        'Any unstable tachycardia requires immediate cardioversion',
        'Patient may deteriorate to cardiac arrest - act quickly'
      ],
      treatment: [
        {
          action: 'Confirm patient is unstable DUE TO tachycardia',
          details: [
            'Hypotension (SBP <90 mmHg)',
            'Acutely altered mental status',
            'Signs of shock (cool extremities, poor perfusion)',
            'Ischemic chest discomfort/pain',
            'Acute heart failure or pulmonary edema',
            'Ensure tachycardia is CAUSING instability (not sinus tach from sepsis, etc.)'
          ]
        },
        {
          action: 'Immediate preparation for synchronized cardioversion',
          details: [
            'Place defibrillator pads on patient',
            'Turn on monitor/defibrillator',
            'Engage SYNCHRONIZED mode (critical - must sync to R wave)',
            'Verify sync markers appearing on QRS complexes',
            'Select appropriate energy (see DRUG DOSING section)',
            'Have crash cart and airway equipment ready'
          ]
        },
        {
          action: 'Consider sedation if time permits',
          details: [
            'If patient conscious and time allows: sedate (etomidate, ketamine, midazolam)',
            'Do NOT delay cardioversion to sedate if critically unstable',
            'If patient unconscious or peri-arrest: proceed immediately',
            'Ensure IV access before sedation if possible'
          ]
        },
        {
          action: 'Perform synchronized cardioversion',
          details: [
            'Ensure oxygen delivery device not in contact with patient',
            'Announce "Shocking on three, all clear"',
            'Verify all personnel clear',
            'Deliver shock while holding paddles/pressing shock button firmly',
            'May need to press/hold to allow sync (device waits for R wave)',
            'Check rhythm and pulse immediately after shock'
          ]
        },
        {
          action: 'Reassess after cardioversion',
          details: [
            'If rhythm converted and patient stable: post-cardioversion care',
            'If no conversion: reconfirm sync mode is ON (may auto-disable after shock)',
            'Increase energy level for subsequent attempts',
            'May give amiodarone 150 mg IV if refractory before next shock',
            'If patient becomes pulseless: switch to defibrillation mode (VF/pVT protocol)'
          ]
        },
        {
          action: 'If cardioversion unsuccessful after multiple attempts',
          details: [
            'Consider antiarrhythmics: amiodarone 150 mg IV over 10 min',
            'Reassess patient stability',
            'Consult cardiology or EP specialist',
            'Consider underlying/reversible causes',
            'Prepare for possible cardiac arrest'
          ]
        }
      ],
      drugs: [
        {
          name: 'Synchronized Cardioversion Energy Levels',
          dose: 'Initial: Narrow regular 50-100J, Narrow irregular 120-200J, Wide regular 100J, Wide irregular 200J (defibrillation dose)',
          notes: 'Follow device recommendations. Escalate energy if initial attempt fails. Biphasic preferred over monophasic. Use highest energy if unstable or unclear rhythm'
        },
        {
          name: 'Sedation for Cardioversion',
          dose: 'Etomidate 0.2-0.3 mg/kg IV OR Midazolam 1-5 mg IV OR Ketamine 1-2 mg/kg IV',
          notes: 'Only if time permits and patient conscious. Do NOT delay cardioversion in critically ill patients. Have airway equipment ready'
        },
        {
          name: 'Amiodarone',
          dose: '150 mg IV over 10 minutes',
          notes: 'For refractory unstable tachycardia between cardioversion attempts. Can cause hypotension. Follow with infusion 1 mg/min if successful'
        },
        {
          name: 'Alternative Sedatives',
          dose: 'Propofol 0.5-1 mg/kg IV OR Fentanyl 50-100 mcg IV',
          notes: 'Propofol may cause hypotension. Fentanyl alone may be insufficient. Use only if experienced and time permits'
        }
      ],
      differential: [
        'Narrow complex regular: SVT/PSVT (AVNRT, AVRT)',
        'Narrow complex irregular: Atrial fibrillation with RVR, atrial flutter',
        'Wide complex regular: Ventricular tachycardia (most common), SVT with aberrancy',
        'Wide complex irregular: Pre-excited A-fib (WPW), polymorphic VT, A-fib with aberrancy',
        'Sinus tachycardia with another cause of instability (sepsis, hypovolemia) - do NOT cardiovert'
      ],
      criticalChanges: [
        {
          condition: 'If patient becomes pulseless during treatment:',
          action: '→ VF/pVT Protocol or → PEA/Asystole Protocol. Switch to unsynchronized defibrillation if VF/pVT. Start CPR immediately'
        },
        {
          condition: 'If rhythm converts to sinus but patient remains unstable:',
          action: 'Tachycardia was NOT the cause of instability. Search for other causes: MI, PE, sepsis, hemorrhage, etc.'
        },
        {
          condition: 'If polymorphic wide complex tachycardia:',
          action: 'Use defibrillation dose (NOT synchronized). May be VF or torsades. If pulseless, treat as VF'
        },
        {
          condition: 'If sinus tachycardia identified:',
          action: 'Do NOT cardiovert sinus tachycardia. Identify and treat underlying cause (sepsis, hypovolemia, pain, hypoxia, PE, etc.)'
        },
        {
          condition: 'If refractory to multiple cardioversion attempts:',
          action: 'Give amiodarone 150 mg IV. Consider transesophageal overdrive pacing for atrial flutter. Consult cardiology/EP. Prepare for possible deterioration'
        }
      ],
      postAcuteCare: [
        {
          title: 'Immediate post-cardioversion assessment',
          content: 'Check rhythm, pulse, blood pressure. Assess mental status. Place on continuous cardiac monitoring. Obtain 12-lead ECG. Check for complications (skin burns, aspiration if sedated)'
        },
        {
          title: 'Maintain sinus rhythm',
          content: 'May need maintenance antiarrhythmic: amiodarone infusion 1 mg/min × 6 hr then 0.5 mg/min, or other agent based on rhythm. Correct electrolytes (K+ >4, Mg2+ >2)'
        },
        {
          title: 'Identify and treat underlying cause',
          content: 'Acute MI (troponin, ECG, consider cath lab). Pulmonary embolism (CT if suspected). Electrolyte abnormalities. Drug toxicity. Hyperthyroidism. Structural heart disease'
        },
        {
          title: 'Anticoagulation for A-fib',
          content: 'If cardioverted from A-fib: assess duration. If >48 hours or unknown: anticoagulation needed (may have cardioverted with clot present). CHA2DS2-VASc score for long-term anticoagulation. Consider TEE'
        },
        {
          title: 'Cardiology consultation',
          content: 'All patients who required emergent cardioversion need cardiology evaluation. EP consultation for recurrent SVT or VT. Consider ICD for sustained VT. Ablation for recurrent SVT or WPW'
        },
        {
          title: 'ICU monitoring',
          content: 'Transfer to monitored setting (ICU or telemetry). Watch for recurrence. Monitor for arrhythmias. Assess for end-organ damage from hypoperfusion period. Serial troponins'
        }
      ],
      notes: [
        'Unstable tachycardia requires IMMEDIATE cardioversion - do not delay for medications',
        'Key is to identify if tachycardia is CAUSING instability vs. associated with it',
        'Sinus tachycardia is physiologic response - cardioversion will not help and may harm',
        'SYNCHRONIZED mode is critical - prevents delivering shock on T wave (can cause VF)',
        'Sync mode may automatically turn OFF after each shock - must re-enable',
        'Higher energy (≥200J) preferred for A-fib/A-flutter to maximize first shock success',
        'For polymorphic VT, use defibrillation (unsynchronized) not cardioversion',
        'Sedation is ideal but do NOT delay cardioversion in critically unstable patients',
        'Unconscious or peri-arrest patients do not need sedation - proceed immediately',
        'Device may "pause" before delivering shock in sync mode (waiting for R wave)',
        'If patient becomes pulseless, immediately switch to CPR and defibrillation mode',
        'After cardioversion, patient may have transient bradycardia or brief pause - normal'
      ],
      references: [
        {
          title: 'Part 9: Adult Advanced Life Support (2025 AHA Guidelines)',
          description: 'American Heart Association CPR & Emergency Cardiovascular Care - Tachycardia Algorithm',
          url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000001376'
        }
      ],
      authors: ['BCM Anesthesia Crisis Manual Committee']
    }
  },

  // ==================== CARDIAC ANESTHESIA CRISIS ====================
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Coronary Air Embolism',
    searchTerms: ['air embolism', 'coronary air', 'de-airing', 'cardiopulmonary bypass'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Acute RV Failure',
    searchTerms: ['right ventricular failure', 'RV failure', 'pulmonary hypertension', 'inhaled NO', 'milrinone'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Acute LV Failure',
    searchTerms: ['left ventricular failure', 'LV failure', 'cardiogenic shock', 'inotropes', 'IABP'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Pulmonary Hypertensive Crisis',
    searchTerms: ['pulmonary hypertension', 'RV failure', 'inhaled NO', 'epoprostenol'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Post-CPB Coagulopathy',
    searchTerms: ['coagulopathy', 'bleeding', 'TEG', 'cardiopulmonary bypass', 'protamine'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Protamine Reaction',
    searchTerms: ['protamine', 'hypotension', 'anaphylaxis', 'pulmonary hypertension'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Difficulty Weaning from CPB',
    searchTerms: ['CPB', 'cardiopulmonary bypass', 'weaning', 'IABP', 'ECMO'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'TEE Probe Complications',
    searchTerms: ['TEE', 'transesophageal echo', 'esophageal perforation'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Aortic Dissection',
    searchTerms: ['aortic dissection', 'type A dissection', 'malperfusion'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Pacemaker/ICD Malfunction',
    searchTerms: ['pacemaker', 'ICD', 'magnet', 'malfunction'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Tamponade Post-Cardiac Surgery',
    searchTerms: ['tamponade', 'pericardial effusion', 'chest reopening'],
    content: {
      immediate: [
        'Alert surgeon immediately - suspected tamponade',
        'Perform EMERGENT echocardiography (TEE or TTE)',
        'Prepare for emergent re-exploration (bedside or OR)',
        'Start volume resuscitation and inotropes'
      ],
      recognition: [
        'Hypotension or narrow pulse pressure (often sudden onset)',
        'Tachycardia disproportionate to volume status',
        'Equalization of filling pressures (CVP = RVEDP = PCWP = LVEDP)',
        'Pulsus paradoxus (>10 mmHg drop in SBP during inspiration on arterial line)',
        'Decreased or absent chest tube output (clotted tubes - classic sign)',
        'Decreased cardiac index on hemodynamic monitoring',
        'ECHO FINDINGS (diagnostic):',
        'Pericardial effusion (even small amounts can cause tamponade post-operatively)',
        'RA systolic collapse (early finding, sensitive)',
        'RV diastolic collapse (more specific for tamponade)',
        'Exaggerated ventricular septal bounce with respiration',
        'Respirophasic variation in mitral/tricuspid inflow velocities (>25%)',
        'IVC plethora (dilated IVC with <50% respiratory collapse)',
        'Possible loculated effusions or organized clot (common post-surgery)'
      ],
      treatment: [
        {
          action: 'Notify surgical team immediately',
          details: [
            'Alert surgeon and entire OR/ICU team',
            'Prepare for emergent chest re-exploration',
            'Decide: bedside (ICU) vs OR re-exploration based on location and acuity',
            'Have sternotomy tray immediately available'
          ]
        },
        {
          action: 'Hemodynamic goals: "Fast, Full, Tight"',
          details: [
            'Fast: Maintain tachycardia (avoid bradycardia with inotropes)',
            'Full: Aggressive volume resuscitation to maintain preload',
            'Tight: High contractility with inotropes (avoid myocardial depression)',
            'Maintain normal sinus rhythm (treat arrhythmias immediately)'
          ]
        },
        {
          action: 'Volume resuscitation',
          details: [
            'Rapid IV crystalloid boluses (500-1000 mL)',
            'Blood products if anemic or coagulopathic',
            'Maintain CVP elevated (goal 12-15 mmHg)',
            'Continue aggressive volume until pericardium opened'
          ]
        },
        {
          action: 'Inotropic support',
          details: [
            'Epinephrine 2-10 mcg/min - first choice',
            'OR Norepinephrine 5-20 mcg/min if needed for BP support',
            'Avoid beta blockers and calcium channel blockers',
            'Goal: maintain HR >80-90 and strong contractility',
            'May need escalating doses until tamponade relieved'
          ]
        },
        {
          action: 'Ventilation strategy',
          details: [
            'Most patients already intubated post-operatively',
            'Positive pressure ventilation reduces venous return and worsens tamponade',
            'Use low tidal volumes (4-6 mL/kg) and minimize PEEP',
            'If extubated: avoid reintubation unless absolutely necessary (worsens hemodynamics)',
            'Prepare for hemodynamic collapse with positive pressure ventilation'
          ]
        }
      ],
      drugs: [
        {
          name: 'Epinephrine',
          dose: '2-10 mcg/min IV infusion',
          notes: 'First-line inotrope. Maintains heart rate and contractility. Titrate to effect. Mix: 1 mg in 250 mL (4 mcg/mL)'
        },
        {
          name: 'Norepinephrine',
          dose: '5-20 mcg/min IV infusion',
          notes: 'Add if blood pressure critically low despite epinephrine. Provides additional vasoconstriction. Mix: 4 mg in 250 mL (16 mcg/mL)'
        },
        {
          name: 'Calcium Chloride',
          dose: '500-1000 mg IV push',
          notes: 'May improve contractility, especially after blood products. Give slowly over 5-10 min if not emergent'
        }
      ],
      differential: [
        'Acute myocardial dysfunction (RV or LV failure)',
        'Massive pulmonary embolism',
        'Tension pneumothorax',
        'Heart block or malignant arrhythmia',
        'Other causes of obstructive shock',
        'Hypovolemia from occult bleeding (not tamponade)',
        'Graft failure or myocardial infarction'
      ],
      criticalChanges: [
        {
          condition: 'If cardiac arrest develops:',
          action: 'IMMEDIATE sternotomy and pericardial decompression. Open chest massage. Follow → Ventricular Fibrillation / Pulseless Ventricular Tachycardia, → Pulseless Electrical Activity (PEA), or → Asystole protocol as appropriate'
        },
        {
          condition: 'If patient too unstable to transport to OR:',
          action: 'Bedside sternotomy in ICU. Have sternotomy tray, suction, and electrocautery ready. Surgeon performs emergency re-exploration at bedside'
        },
        {
          condition: 'If hypotension worsens despite volume and inotropes:',
          action: 'Do NOT delay re-exploration. Increasing doses of pressors will not fix tamponade. Surgical decompression is definitive treatment'
        },
        {
          condition: 'If chest tubes suddenly stop draining:',
          action: 'Suspect clotted tubes causing tamponade. Attempt to clear tubes. Prepare for re-exploration regardless'
        },
        {
          condition: 'If bradycardia develops:',
          action: 'Increase epinephrine immediately. Consider atropine 0.5-1 mg IV. Bradycardia is ominous sign - expedite surgical intervention'
        }
      ],
      postAcuteCare: [
        {
          title: 'Monitor for recurrent tamponade',
          content: 'Serial echocardiography q6-12h for first 24-48 hours. Watch for return of equalized pressures, decreased cardiac output, or hemodynamic deterioration. Recurrence rate ~10-20%'
        },
        {
          title: 'Chest tube management',
          content: 'Ensure tubes patent and functioning. Monitor output closely. Milk/strip tubes per surgeon preference. Consider increased anticoagulation cautiously once stable'
        },
        {
          title: 'Correct coagulopathy',
          content: 'Check coags q6h until stable. Target: INR <1.5, PTT <40 sec, platelets >100,000, fibrinogen >200. Treat with FFP, platelets, cryoprecipitate as needed'
        },
        {
          title: 'Hemodynamic weaning',
          content: 'Slowly wean inotropes once definitively treated. Monitor for return of tamponade physiology. May need several days of inotropic support'
        },
        {
          title: 'Assess for complications',
          content: 'Monitor for: bleeding requiring re-exploration, wound infection, sternal instability, arrhythmias, renal dysfunction, neurologic complications'
        },
        {
          title: 'ICU monitoring and ventilator management',
          content: 'Continuous arterial line and CVP monitoring. Early extubation if possible to reduce positive pressure effects. Monitor end-organ perfusion (urine output, lactate, mental status)'
        }
      ],
      notes: [
        'Post-cardiac surgery tamponade is a SURGICAL EMERGENCY requiring immediate re-exploration',
        'Hemodynamic goals: "Fast, Full, Tight" - maintain HR, preload, and contractility',
        'Volume resuscitation is temporizing - definitive treatment is surgical drainage',
        'LOCALIZED/REGIONAL tamponade unique to post-surgical patients: blood clot behind specific chamber (e.g., LA or RA) causing tamponade physiology WITHOUT large circumferential effusion',
        'Echo may show tamponade physiology (chamber collapse) even with small or localized fluid collection',
        'Sudden cessation of chest tube drainage is classic sign (clotted tubes)',
        'Echo is CRITICAL for diagnosis - RV diastolic collapse is most specific finding',
        'Bedside re-exploration may be lifesaving if patient too unstable for OR transport',
        'Positive pressure ventilation worsens tamponade - maintain spontaneous ventilation when possible',
        'Pericardiocentesis is NOT the treatment for post-surgical tamponade (loculated clot)',
        'Equalization of pressures (CVP = PCWP) is highly specific hemodynamic finding',
        'Even small amounts of fluid (50-100 mL) can cause tamponade in acute post-op setting',
        'Do NOT delay re-exploration to "optimize" patient - surgery is the optimization'
      ],
      references: [
        {
          title: 'Management of Cardiac Tamponade After Cardiac Surgery (JCVA 2011)',
          description: 'Updated synopsis on pathophysiology, diagnosis, and management including regional/localized tamponade',
          url: 'https://pubmed.ncbi.nlm.nih.gov/21868250/'
        },
        {
          title: 'Incidence, Predictors, and Outcomes of Postoperative Cardiac Tamponade (PLOS ONE 2016)',
          description: 'Large cohort study examining risk factors and clinical outcomes after heart valve surgery',
          url: 'https://pubmed.ncbi.nlm.nih.gov/27855225/'
        },
        {
          title: 'ASE Guidelines for Imaging of Patients with Pericardial Disease (J Am Soc Echo 2013)',
          description: 'Comprehensive echocardiographic guidelines by Klein et al. for pericardial effusion and tamponade',
          url: 'https://pubmed.ncbi.nlm.nih.gov/23998693/'
        }
      ],
      authors: ['Will Kang, MD', 'Thomas Powell, MD']
    }
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'VAD Emergency',
    searchTerms: ['VAD', 'ventricular assist device', 'LVAD', 'RVAD', 'BiVAD'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'ECMO Emergency',
    searchTerms: ['ECMO', 'extracorporeal membrane oxygenation', 'cannulation', 'circuit'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Acute Myocardial Ischemia Intraoperatively',
    searchTerms: ['myocardial ischemia', 'MI', 'ST elevation', 'nitroglycerin'],
    content: null
  },
  {
    categoryId: 'cardiac',
    category: 'Cardiac Anesthesia Crisis',
    title: 'Paralysis after TAAA Repair',
    searchTerms: ['TAAA', 'thoracoabdominal aortic aneurysm', 'spinal cord ischemia', 'paralysis', 'CSF drain'],
    content: null
  },

  // ==================== THORACIC SURGERY ====================
  {
    categoryId: 'thoracic',
    category: 'Thoracic Surgery',
    title: 'Hypoxemia During One Lung Ventilation',
    searchTerms: ['OLV', 'one lung ventilation', 'hypoxemia', 'desaturation', 'double lumen tube'],
    content: null
  },
  {
    categoryId: 'thoracic',
    category: 'Thoracic Surgery',
    title: 'Double Lumen Tube Malposition',
    searchTerms: ['DLT', 'double lumen tube', 'malposition', 'fiberoptic', 'bronchoscopy'],
    content: null
  },
  {
    categoryId: 'thoracic',
    category: 'Thoracic Surgery',
    title: 'Massive Hemoptysis',
    searchTerms: ['hemoptysis', 'airway bleeding', 'double lumen tube', 'bronchial blocker'],
    content: null
  },
  {
    categoryId: 'thoracic',
    category: 'Thoracic Surgery',
    title: 'Mediastinal Mass with Airway Compromise',
    searchTerms: ['mediastinal mass', 'anterior mediastinal mass', 'airway obstruction', 'cardiopulmonary bypass'],
    content: null
  },

  // ==================== PERFUSION / CPB ====================
  {
    categoryId: 'perfusion',
    category: 'Perfusion / CPB',
    title: 'Oxygenator Failure',
    searchTerms: ['oxygenator', 'CPB', 'hypoxemia', 'membrane lung'],
    content: null
  },
  {
    categoryId: 'perfusion',
    category: 'Perfusion / CPB',
    title: 'Pump Failure During CPB',
    searchTerms: ['pump failure', 'CPB', 'roller pump', 'centrifugal pump'],
    content: null
  },
  {
    categoryId: 'perfusion',
    category: 'Perfusion / CPB',
    title: 'Arterial Cannula Malposition/Dissection',
    searchTerms: ['arterial cannula', 'aortic dissection', 'malperfusion', 'CPB'],
    content: null
  },
  {
    categoryId: 'perfusion',
    category: 'Perfusion / CPB',
    title: 'Venous Cannula Air Entrainment',
    searchTerms: ['air entrainment', 'venous cannula', 'CPB', 'air lock'],
    content: null
  },
  {
    categoryId: 'perfusion',
    category: 'Perfusion / CPB',
    title: 'Inadequate Venous Drainage on CPB',
    searchTerms: ['venous drainage', 'CPB', 'cannula position', 'kinked line'],
    content: null
  },
  {
    categoryId: 'perfusion',
    category: 'Perfusion / CPB',
    title: 'Massive Gas Embolism During CPB',
    searchTerms: ['gas embolism', 'air embolism', 'CPB', 'de-airing'],
    content: null
  },

  // ==================== OBSTETRIC EMERGENCIES ====================
  {
    categoryId: 'obstetric',
    category: 'Obstetric Emergencies',
    title: 'Postpartum Hemorrhage',
    searchTerms: ['PPH', 'postpartum hemorrhage', 'uterine atony', 'oxytocin', 'methergine'],
    content: null
  },
  {
    categoryId: 'obstetric',
    category: 'Obstetric Emergencies',
    title: 'Emergency C-Section (Working Epidural)',
    searchTerms: ['emergency c-section', 'epidural', 'cesarean', 'category 1'],
    content: null
  },
  {
    categoryId: 'obstetric',
    category: 'Obstetric Emergencies',
    title: 'Emergency C-Section (No Epidural)',
    searchTerms: ['emergency c-section', 'general anesthesia', 'cesarean', 'crash section'],
    content: null
  },
  {
    categoryId: 'obstetric',
    category: 'Obstetric Emergencies',
    title: 'Maternal Cardiac Arrest / Perimortem C-Section',
    searchTerms: ['maternal cardiac arrest', 'perimortem cesarean', 'PMCD', 'resuscitative hysterotomy'],
    content: null
  },
  {
    categoryId: 'obstetric',
    category: 'Obstetric Emergencies',
    title: 'Severe Preeclampsia / Eclampsia',
    searchTerms: ['preeclampsia', 'eclampsia', 'seizure', 'magnesium', 'HELLP'],
    content: null
  },

  // ==================== NEUROANESTHESIA ====================
  {
    categoryId: 'neuro',
    category: 'Neuroanesthesia',
    title: 'Lost MEP/SSEP Signals',
    searchTerms: ['MEP', 'SSEP', 'neuromonitoring', 'evoked potentials', 'motor evoked', 'somatosensory'],
    content: null
  }
];
